/* Tour event bus
   ---------------
   Tiny pub/sub used by the Timesheet Guided Tour so individual
   components (TimesheetHeader, AddTimesheetSidebar, TimesheetCreation…)
   don't need to receive tour-aware props. Components emit named
   events on user actions ("client-created", "submit-success", etc.)
   and the tour hook subscribes to advance steps automatically.

   Decoupling rule: components never import the tour hook. They only
   emit. The tour decides what to do with the event. */

export type TourEvent =
  | 'client-created'
  | 'timesheet-record-clicked'
  | 'sidebar-opened'
  | 'sidebar-closed'
  | 'submit-success'
  | 'add-entry-clicked'
  | 'save-clicked'
  | 'confirm-yes-clicked'
  /* Fires when the AddCustomerModal becomes visible — used in the 2A
     "no-client" branch so the tour can advance from highlighting the
     Add Client button to highlighting the modal itself. */
  | 'add-customer-modal-opened'
  /* Lifecycle events the hook itself emits so other components can
     react (e.g. TimesheetCreation expands collapsed weeks on open). */
  | 'tour:opened'
  | 'tour:closed'
  /* Fired ONLY when the user clicks the explicit Skip Tour button in
     the bubble — not when the × button is clicked. The page listens
     and shows a toast + a pulse animation on the Start Tour CTA so
     the user knows where to come back. */
  | 'tour:skipped';

type Listener = () => void;
const listeners = new Map<TourEvent, Set<Listener>>();

export const emit = (event: TourEvent): void => {
  const set = listeners.get(event);
  if (!set) return;
  // Snapshot before iterating in case a listener unsubscribes itself.
  Array.from(set).forEach((fn) => {
    try { fn(); } catch (_) { /* swallow — one bad listener shouldn't stop the rest */ }
  });
};

export const subscribe = (event: TourEvent, fn: Listener): (() => void) => {
  let set = listeners.get(event);
  if (!set) {
    set = new Set();
    listeners.set(event, set);
  }
  set.add(fn);
  return () => {
    const s = listeners.get(event);
    if (s) s.delete(fn);
  };
};
